/**
 * BottomNavBar.js - Mobile bottom navigation component
 * Provides thumb-friendly navigation for mobile users
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, Calendar, MessageSquare, User, MoreHorizontal, X,
  Briefcase, Clock, DollarSign, Users, Settings, FileText,
  Star, Heart, Upload, Award, AlertTriangle, CreditCard,
  ClipboardList, MapPin, Phone, BookOpen, Shield, UserCheck,
  BarChart3, Headphones, FolderOpen, Package, Truck, Sparkles,
  CheckCircle, Bell, HelpCircle, Receipt, GraduationCap, Scale,
  Plus, Tag, CalendarDays, UserPlus, ClipboardCheck, ScrollText,
  PhoneOff, Mail, FileEdit, Image, MessageCircle, Flag, History,
  Archive, Banknote, Car, UserX, ShoppingBag, Shirt
} from 'lucide-react';

// Icon mapping for dynamic icon rendering
const iconMap = {
  Home, Calendar, MessageSquare, User, MoreHorizontal, X,
  Briefcase, Clock, DollarSign, Users, Settings, FileText,
  Star, Heart, Upload, Award, AlertTriangle, CreditCard,
  ClipboardList, MapPin, Phone, BookOpen, Shield, UserCheck,
  BarChart3, Headphones, FolderOpen, Package, Truck, Sparkles,
  CheckCircle, Bell, HelpCircle, Receipt, GraduationCap, Scale,
  Plus, Tag, CalendarDays, UserPlus, ClipboardCheck, ScrollText,
  PhoneOff, Mail, FileEdit, Image, MessageCircle, Flag, History,
  Archive, Banknote, Car, UserX, ShoppingBag, Shirt
};

const BottomNavBar = ({ 
  tabs, 
  activeTab, 
  onTabChange, 
  moreMenuItems = [],
  badge = {} // { tabId: count } for notification badges
}) => {
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Check if 'more' is in the tabs array as a main tab
  const hasMoreTab = tabs.some(tab => tab.id === 'more');
  const mainTabs = hasMoreTab ? tabs.slice(0, 5) : tabs.slice(0, 4); // Show all 5 if 'more' is included
  const hasMore = moreMenuItems.length > 0; // Show slide-up menu when there are more items

  const getIcon = (iconName, isActive) => {
    const IconComponent = typeof iconName === 'string' ? iconMap[iconName] : iconName;
    if (!IconComponent) return null;
    return (
      <IconComponent 
        className={`w-5 h-5 transition-colors ${
          isActive ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'
        }`} 
      />
    );
  };

  const handleTabClick = (tabId) => {
    // Navigate to the tab (including 'more' which will show the More screen)
    onTabChange(tabId);
    // Scroll to top when changing tabs
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMoreItemClick = (itemId) => {
    onTabChange(itemId);
    setShowMoreMenu(false);
    // Scroll to top when changing tabs
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[var(--background-paper)]/95 backdrop-blur-xl border-t border-[var(--border-primary)] z-40" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex items-stretch justify-around h-16 px-1">
          {mainTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const badgeCount = badge[tab.id];
            
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`flex flex-col items-center justify-center flex-1 min-h-[56px] py-2 relative transition-all touch-manipulation ${
                  isActive ? 'scale-105' : 'opacity-70 hover:opacity-100 active:scale-95'
                }`}
                data-testid={`bottom-nav-${tab.id}`}
              >
                <div className="relative">
                  {getIcon(tab.icon, isActive)}
                  {badgeCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] mt-1 font-medium transition-colors ${
                  isActive ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'
                }`}>
                  {tab.label}
                </span>
                {isActive && (
                  <motion.div 
                    layoutId="bottomNavIndicator"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[var(--primary)] rounded-full"
                  />
                )}
              </button>
            );
          })}
          
          {/* More Button - Only show if 'more' is NOT in the main tabs */}
          {hasMore && !hasMoreTab && (
            <button
              onClick={() => setShowMoreMenu(true)}
              className={`flex flex-col items-center justify-center flex-1 min-h-[56px] py-2 relative transition-all touch-manipulation ${
                showMoreMenu ? 'scale-105' : 'opacity-70 hover:opacity-100 active:scale-95'
              }`}
              data-testid="bottom-nav-more-btn"
            >
              <MoreHorizontal className={`w-5 h-5 ${showMoreMenu ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'}`} />
              <span className={`text-[10px] mt-1 font-medium ${showMoreMenu ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'}`}>
                More
              </span>
            </button>
          )}
        </div>
      </nav>

      {/* More Menu Overlay */}
      <AnimatePresence>
        {showMoreMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMoreMenu(false)}
              className="md:hidden fixed inset-0 bg-black/60 z-50"
            />
            
            {/* Slide-up Menu */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="md:hidden fixed bottom-0 left-0 right-0 bg-[var(--background-paper)] rounded-t-3xl z-50 max-h-[70vh] overflow-hidden"
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-[var(--border)] rounded-full" />
              </div>
              
              {/* Header */}
              <div className="flex items-center justify-between px-6 pb-4 border-b border-[var(--border)]">
                <h3 className="text-lg font-semibold">More Options</h3>
                <button 
                  onClick={() => setShowMoreMenu(false)}
                  className="p-2 hover:bg-[var(--accent)] rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Menu Items */}
              <div className="overflow-y-auto max-h-[calc(70vh-80px)] pb-8 px-4">
                {moreMenuItems.map((item, index) => {
                  // Handle dividers (section headers)
                  if (item.isDivider) {
                    return (
                      <div key={item.id} className={`${index > 0 ? 'mt-4 pt-4 border-t border-[var(--border)]' : ''}`}>
                        <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2 px-2">
                          {item.label}
                        </p>
                      </div>
                    );
                  }
                  
                  const isActive = activeTab === item.id;
                  const badgeCount = badge[item.id];
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleMoreItemClick(item.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all mb-1 ${
                        isActive 
                          ? 'bg-[var(--primary)]/20 border border-[var(--primary)]' 
                          : 'hover:bg-[var(--accent)]'
                      }`}
                      data-testid={`more-menu-${item.id}`}
                    >
                      <div className="relative">
                        {getIcon(item.icon, isActive)}
                        {badgeCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                            {badgeCount > 9 ? '9+' : badgeCount}
                          </span>
                        )}
                      </div>
                      <span className={`text-sm font-medium ${
                        isActive ? 'text-[var(--primary)]' : 'text-[var(--text-secondary)]'
                      }`}>
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer to prevent content from being hidden behind nav */}
      <div className="md:hidden h-16" />
    </>
  );
};

export default BottomNavBar;
